import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { Metadata, RunView } from '@memberjunction/core';
import { Subject } from 'rxjs';
import { IzzyScopeEntity } from 'mj_generatedentities';
import { IAdminPanel, AdminPanelStatus, DEFAULT_ADMIN_PANEL_STATUS } from '../admin-panel.interface';

interface ScopeDisplay {
  scope: IzzyScopeEntity;
  isEditing: boolean;
  editForm: ScopeEditForm;
}

interface ScopeEditForm {
  name: string;
  description: string;
}

@Component({
  standalone: false,
  selector: 'izzy-scopes',
  templateUrl: './scopes.component.html',
  styleUrls: ['./scopes.component.css']
})
export class ScopesComponent implements OnInit, OnDestroy, IAdminPanel {
  @Input() initialState: any = null;
  @Output() stateChange = new EventEmitter<any>();
  @Output() statusChange = new EventEmitter<AdminPanelStatus>();
  @Output() interaction = new EventEmitter<any>();

  constructor() {}

  protected scopes: IzzyScopeEntity[] = [];
  protected scopeDisplays: ScopeDisplay[] = [];

  protected isLoading: boolean = true;
  protected isSaving: boolean = false;
  protected errorMessage: string = '';
  protected successMessage: string = '';
  protected searchQuery: string = '';

  protected isCreating: boolean = false;
  protected createForm: ScopeEditForm = this.getEmptyForm();

  protected confirmDialogVisible: boolean = false;
  protected confirmDialogTitle: string = '';
  protected confirmDialogMessage: string = '';
  protected confirmDialogAction: (() => Promise<void>) | null = null;

  private destroy$ = new Subject<void>();

  async ngOnInit(): Promise<void> {
    await this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getEmptyForm(): ScopeEditForm {
    return {
      name: '',
      description: ''
    };
  }

  protected async loadData(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';

    try {
      const rv = new RunView();
      const result = await rv.RunView<IzzyScopeEntity>({
        EntityName: 'Scopes',
        OrderBy: 'Name ASC'
      });
      this.scopes = result.Results;
      this.scopeDisplays = this.scopes.map(s => ({
        scope: s,
        isEditing: false,
        editForm: this.scopeToForm(s)
      }));

      // Emit default status (scopes don't have a health indicator)
      this.statusChange.emit(DEFAULT_ADMIN_PANEL_STATUS);
    } catch (error) {
      console.error('Error loading scopes:', error);
      this.errorMessage = 'Failed to load scopes.';
    } finally {
      this.isLoading = false;
    }
  }

  private scopeToForm(scope: IzzyScopeEntity): ScopeEditForm {
    return {
      name: scope.Name,
      description: scope.Description || ''
    };
  }

  get filteredScopes(): ScopeDisplay[] {
    return this.scopeDisplays.filter(sd => {
      const matchesSearch = !this.searchQuery.trim() ||
        sd.scope.Name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        (sd.scope.Description && sd.scope.Description.toLowerCase().includes(this.searchQuery.toLowerCase()));
      return matchesSearch;
    });
  }

  startCreate(): void {
    this.isCreating = true;
    this.createForm = this.getEmptyForm();
  }

  cancelCreate(): void {
    this.isCreating = false;
    this.createForm = this.getEmptyForm();
  }

  async saveCreate(): Promise<void> {
    if (!this.createForm.name.trim()) {
      this.errorMessage = 'Scope name is required.';
      return;
    }

    this.isSaving = true;
    try {
      const md = new Metadata();
      const newScope = await md.GetEntityObject<IzzyScopeEntity>('Scopes');
      newScope.Name = this.createForm.name.trim();
      newScope.Description = this.createForm.description.trim() || null;

      await newScope.Save();
      await this.loadData();
      this.isCreating = false;
      this.showSuccess('Scope created');
    } catch (error) {
      console.error('Error creating scope:', error);
      this.errorMessage = 'Failed to create scope.';
    } finally {
      this.isSaving = false;
    }
  }

  startEdit(display: ScopeDisplay): void {
    display.isEditing = true;
    display.editForm = this.scopeToForm(display.scope);
  }

  cancelEdit(display: ScopeDisplay): void {
    display.isEditing = false;
  }

  async saveEdit(display: ScopeDisplay): Promise<void> {
    this.isSaving = true;
    try {
      display.scope.Name = display.editForm.name.trim();
      display.scope.Description = display.editForm.description.trim() || null;

      await display.scope.Save();
      display.isEditing = false;
      this.showSuccess('Scope updated');
    } catch (error) {
      console.error('Error updating scope:', error);
      this.errorMessage = 'Failed to update scope.';
    } finally {
      this.isSaving = false;
    }
  }

  confirmDelete(display: ScopeDisplay): void {
    this.showConfirmDialog(
      'Delete Scope',
      `Are you sure you want to delete the scope "${display.scope.Name}"? This will remove it from all API keys.`,
      async () => {
        this.isSaving = true;
        try {
          await display.scope.Delete();
          await this.loadData();
          this.showSuccess('Scope deleted');
        } catch (error) {
          console.error('Error deleting scope:', error);
          this.errorMessage = 'Failed to delete scope.';
        } finally {
          this.isSaving = false;
        }
      }
    );
  }

  getScopeIcon(name: string): string {
    const lowerName = name.toLowerCase();
    if (lowerName.includes('read')) return 'fa-eye';
    if (lowerName.includes('write') || lowerName.includes('create')) return 'fa-pen';
    if (lowerName.includes('delete')) return 'fa-trash';
    if (lowerName.includes('admin')) return 'fa-shield-alt';
    if (lowerName.includes('message')) return 'fa-envelope';
    if (lowerName.includes('channel')) return 'fa-broadcast-tower';
    if (lowerName.includes('org')) return 'fa-building';
    return 'fa-key';
  }

  protected showConfirmDialog(title: string, message: string, action: () => Promise<void>): void {
    this.confirmDialogTitle = title;
    this.confirmDialogMessage = message;
    this.confirmDialogAction = action;
    this.confirmDialogVisible = true;
  }

  protected async confirmDialogYes(): Promise<void> {
    if (this.confirmDialogAction) {
      await this.confirmDialogAction();
    }
    this.closeConfirmDialog();
  }

  protected closeConfirmDialog(): void {
    this.confirmDialogVisible = false;
    this.confirmDialogAction = null;
  }

  private showSuccess(message: string): void {
    this.successMessage = message;
    setTimeout(() => { this.successMessage = ''; }, 3000);
  }
}

export function LoadScopesComponent(): void {}
